/**
 * Network chip for the top bar: ONLINE, OFFLINE, or OFFLINE READY (offline with
 * the shell and the bundled data cached, so every mode still boots). The tooltip
 * carries the detail; a Reload button appears when a new version is waiting.
 */
import { CloudCheck, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { Chip } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { BADGE_LABEL, badgeState, type OfflineBadgeState } from './state';
import { useOffline } from './store';

const TONE = { online: 'nominal', 'offline-ready': 'caution', offline: 'warning' } as const;
const ICON = { online: Wifi, 'offline-ready': CloudCheck, offline: WifiOff } as const;

function tooltip(state: OfflineBadgeState, offlineReady: boolean, tilesCached: number): string {
  const tiles = `${tilesCached} map tile${tilesCached === 1 ? '' : 's'} cached`;
  if (state === 'online')
    return offlineReady
      ? `Online. Offline ready: app shell and bundled data cached, ${tiles}.`
      : 'Online. The offline cache is not complete yet.';
  if (state === 'offline-ready')
    return `Offline, running from the cache (${tiles}). Live feeds and GAIA need the network.`;
  return 'Offline, and the app is not fully cached: some modes may not load.';
}

/** `compact` shows the icon only (the label stays for screen readers and in the tooltip) for a crowded top bar. */
export function OfflineBadge({ className, compact = false }: { className?: string; compact?: boolean }) {
  const online = useOffline((s) => s.online);
  const offlineReady = useOffline((s) => s.offlineReady);
  const tilesCached = useOffline((s) => s.tilesCached);
  const updateReady = useOffline((s) => s.updateReady);
  const applyUpdate = useOffline((s) => s.applyUpdate);
  const state = badgeState({ online, offlineReady });
  const Icon = ICON[state];

  return (
    <span
      className={cn('inline-flex items-center gap-1', className)}
      role="status"
      title={tooltip(state, offlineReady, tilesCached)}
    >
      <Chip tone={TONE[state]}>
        <Icon className="h-3 w-3" aria-hidden />
        <span data-testid="offline-badge" className={compact ? 'sr-only' : undefined}>
          {BADGE_LABEL[state]}
        </span>
      </Chip>
      {updateReady && (
        <button
          type="button"
          onClick={applyUpdate}
          title="A new version is installed: reload to use it"
          className="inline-flex items-center gap-1 rounded-full border border-accent/40 px-2 py-0.5 text-[11px] font-medium text-accent transition hover:bg-accent-soft"
        >
          <RefreshCw className="h-3 w-3" aria-hidden />
          Reload
        </button>
      )}
    </span>
  );
}
